import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTimezone } from '../../context/TimezoneContext';

// Custom styles for react-datepicker to work with Tailwind
const datePickerStyles = `
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  
  .react-datepicker__input-container input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
  
  .react-datepicker {
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  
  .react-datepicker__header {
    background-color: #f9fafb;
    border-bottom: 1px solid #d1d5db;
    border-radius: 0.5rem 0.5rem 0 0;
  }
  
  .react-datepicker__day--selected {
    background-color: #3b82f6;
    color: white;
  }
  
  .react-datepicker__day--selected:hover {
    background-color: #2563eb;
  }
  
  .react-datepicker__day:hover {
    background-color: #f3f4f6;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = datePickerStyles;
  document.head.appendChild(styleSheet);
}

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  includeTime?: boolean;
  format?: 'DD/MM/YYYY' | 'MM/DD/YYYY';
  use24Hour?: boolean;
  className?: string;
  placeholder?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  label,
  required = false,
  includeTime = true,
  format,
  use24Hour,
  className = '',
  placeholder,
}) => {
  const { timezone } = useTimezone();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Detect date format from browser locale
  const detectDateFormat = (): 'DD/MM/YYYY' | 'MM/DD/YYYY' => {
    if (format) return format;
    
    const testDate = new Date(2023, 11, 25); // December 25, 2023
    const formatted = new Intl.DateTimeFormat().format(testDate);
    
    // Check if day comes before month (DD/MM/YYYY)
    if (formatted.startsWith('25')) {
      return 'DD/MM/YYYY';
    }
    return 'MM/DD/YYYY';
  };

  // Detect 12h/24h preference from browser locale
  const detect12Hour = (): boolean => {
    if (use24Hour !== undefined) return !use24Hour;
    
    // Check the browser's default hour12 setting
    const options = new Intl.DateTimeFormat().resolvedOptions();
    return options.hour12 === true;
  };

  const detectedFormat = detectDateFormat();
  const is12Hour = detect12Hour();

  // Convert ISO string to Date object
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    
    if (date) {
      // If includeTime is false, set time to 00:00
      if (!includeTime) {
        date.setHours(0, 0, 0, 0);
      } else {
        // If time is 00:00 (default), set it to 12:00 PM
        if (date.getHours() === 0 && date.getMinutes() === 0) {
          date.setHours(12, 0, 0, 0);
        }
      }
      
      // Convert to ISO string
      const isoString = date.toISOString();
      onChange(isoString);
    } else {
      onChange('');
    }
  };

  // Format configuration for react-datepicker
  const getDateFormat = () => {
    if (detectedFormat === 'DD/MM/YYYY') {
      return includeTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
    }
    return includeTime ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy';
  };

  const getTimeFormat = () => {
    return is12Hour ? 'h:mm aa' : 'HH:mm';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          showTimeSelect={includeTime}
          timeFormat={getTimeFormat()}
          dateFormat={getDateFormat()}
          timeIntervals={15}
          timeCaption="Time"
          placeholderText={placeholder || `Select ${includeTime ? 'date and time' : 'date'}`}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          required={required}
          isClearable
          showPopperArrow={false}
        />
      </div>
      
      <p className="text-xs text-gray-500">
        Format: {detectedFormat} {includeTime && (is12Hour ? '12h' : '24h')} • Timezone: {timezone}
      </p>
    </div>
  );
};

export default DateTimePicker;
